import { useEffect, useState } from 'react';

import { api } from '../api/client';
import type { Account } from '../types';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function ProfitLoss() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    async function fetchAccounts() {
      try {
        const data = await api.accounts.list();
        setAccounts(data);
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      } finally {
        setLoading(false);
      }
    }
    void fetchAccounts();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Math.abs(amount));
  };

  // Group accounts
  const revenue = accounts.filter(a => a.type === 'REVENUE');
  const expenses = accounts.filter(a => a.type === 'EXPENSE');

  const totalRevenue = revenue.reduce((sum, a) => sum + Math.abs(a.balance), 0);
  const totalExpenses = expenses.reduce((sum, a) => sum + a.balance, 0);
  const netIncome = totalRevenue - totalExpenses;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading profit & loss...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Profit & Loss</h1>
          <p className="text-sm text-muted-foreground">Income Statement</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start date"
          />
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End date"
          />
          <Button variant="outline" size="sm">Export CSV</Button>
          <Button variant="default" size="sm">Print</Button>
        </div>
      </div>

      {startDate && endDate && (
        <p className="text-sm text-muted-foreground">
          For the period {new Date(startDate).toLocaleDateString('en-CA')} to {new Date(endDate).toLocaleDateString('en-CA')}
        </p>
      )}

      {/* Revenue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-arrow-down-circle"></i> Revenue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                  <th className="py-2 px-3 text-right font-medium text-muted-foreground">Amount</th>
                </tr>
              </thead>
              <tbody>
                {revenue.map((account) => (
                  <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-2 px-3">
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
                      {account.name}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-green-600">
                      {formatCurrency(account.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold bg-green-50 dark:bg-green-950/20">
                  <td className="py-2 px-3">Total Revenue</td>
                  <td className="py-2 px-3 text-right font-mono">{formatCurrency(totalRevenue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Expenses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-arrow-up-circle"></i> Expenses
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                  <th className="py-2 px-3 text-right font-medium text-muted-foreground">Amount</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((account) => (
                  <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-2 px-3">
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
                      {account.name}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-red-600">
                      {formatCurrency(account.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold bg-red-50 dark:bg-red-950/20">
                  <td className="py-2 px-3">Total Expenses</td>
                  <td className="py-2 px-3 text-right font-mono">{formatCurrency(totalExpenses)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Net Income */}
      <Card className={netIncome >= 0 ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : 'border-red-500 bg-red-50 dark:bg-red-950/20'}>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Net {netIncome >= 0 ? 'Income' : 'Loss'}</h3>
              <p className="text-sm text-muted-foreground">Total Revenue - Total Expenses</p>
            </div>
            <div className={`text-2xl font-bold font-mono ${netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {netIncome >= 0 ? '+' : '-'}{formatCurrency(netIncome)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}